import { useEffect, useMemo, useRef } from "react";
import { cls } from "itsf-ui-common";
import { HELP_SUPPORT_URL } from "@constants";
import type { ISweeprOtcPayload } from "@pages/Home/type/ISweeprOtcPayload";
import { getSweeprWidgetUrls } from "@pages/Home/HomeMenuModal/sweepr/getSweeprWidgetUrls";
import { useStyle } from "./style";

interface IParams {
    onInteractionEnd(): void;
    otcPayload: ISweeprOtcPayload | null;
}

const SWEEPR_WIDGET_SCRIPT_ID = "sweepr-widget-script";
const SWEEPR_WIDGET_STYLESHEET_ID = "sweepr-widget-stylesheet";

const removeSweeprIframesFrom = (container: HTMLElement | null) => {
    if (!container) {
        return;
    }

    container.querySelectorAll("iframe").forEach((iframe) => {
        iframe.remove();
    });
};

const ensureSweeprStylesheet = (widgetCssUrl: string) => {
    if (document.getElementById(SWEEPR_WIDGET_STYLESHEET_ID)) {
        return;
    }

    const stylesheet = document.createElement("link");
    stylesheet.id = SWEEPR_WIDGET_STYLESHEET_ID;
    stylesheet.rel = "stylesheet";
    stylesheet.type = "text/css";
    stylesheet.href = widgetCssUrl;
    document.head.appendChild(stylesheet);
};

let sweeprScriptPromise: Promise<void> | null = null;

const ensureSweeprScriptLoaded = (widgetJsUrl: string): Promise<void> => {
    if (!document.getElementById(SWEEPR_WIDGET_SCRIPT_ID)) {
        sweeprScriptPromise = null;
    }

    if (window.SweeprWidget) {
        return Promise.resolve();
    }

    if (sweeprScriptPromise) {
        return sweeprScriptPromise;
    }

    sweeprScriptPromise = new Promise<void>((resolve, reject) => {
        const existingScript = document.getElementById(SWEEPR_WIDGET_SCRIPT_ID) as HTMLScriptElement | null;

        const onLoad = () => {
            resolve();
        };

        const onError = () => {
            sweeprScriptPromise = null;
            reject(new Error("Failed to load Sweepr widget script"));
        };

        if (existingScript) {
            existingScript.addEventListener("load", onLoad, { once: true });
            existingScript.addEventListener("error", onError, { once: true });

            return;
        }

        const script = document.createElement("script");
        script.id = SWEEPR_WIDGET_SCRIPT_ID;
        script.src = widgetJsUrl;
        script.async = true;
        script.addEventListener("load", onLoad, { once: true });
        script.addEventListener("error", onError, { once: true });
        document.body.appendChild(script);
    });

    return sweeprScriptPromise;
};

const initializeSweeprWidget = () => {
    const SweeprWidgetConstructor = window.SweeprWidget;

    if (!SweeprWidgetConstructor) {
        return;
    }

    window.sweeprWidgetBuilt = false;
    new SweeprWidgetConstructor();
};

export const SweeprWidgetContent = ({ otcPayload, onInteractionEnd }: IParams) => {
    const { classes } = useStyle();
    const containerRef = useRef<HTMLDivElement>(null);
    const { widgetApplicationUrl, widgetCssUrl, widgetJsUrl } = getSweeprWidgetUrls();

    const widgetOtc = useMemo(() => (otcPayload?.onetimecode ? JSON.stringify(otcPayload.onetimecode) : ""), [
        otcPayload?.onetimecode,
    ]);

    useEffect(() => {
        window.sweeprCallback = onInteractionEnd;

        return () => {
            delete window.sweeprCallback;
        };
    }, [onInteractionEnd]);

    useEffect(() => {
        let cancelled = false;
        const container = containerRef.current;

        const run = async () => {
            ensureSweeprStylesheet(widgetCssUrl);

            try {
                await ensureSweeprScriptLoaded(widgetJsUrl);
            } catch {
                return;
            }

            if (cancelled) {
                return;
            }

            removeSweeprIframesFrom(container);
            initializeSweeprWidget();
        };

        void run();

        return () => {
            cancelled = true;
            removeSweeprIframesFrom(container);
        };
    }, [widgetCssUrl, widgetJsUrl, widgetOtc]);

    return (
        <div className={classes.sweeprWidgetContainer} ref={containerRef}>
            <div
                className={cls("sweepr-widget", "type-modal", classes.sweeprWidget)}
                data-interaction-end-callback="sweeprCallback"
                data-support-resources-url={HELP_SUPPORT_URL}
                data-sweepr-utterance=""
                data-widget-display-home="true"
                data-widget-otc={widgetOtc}
                data-widget-static-size="true"
                data-widget-type="modal"
                data-widget-url={widgetApplicationUrl}
                data-widget-visible="true"
                id="sweepr-widget"
            />
        </div>
    );
};
